// The selected piece's card in the builder's left panel: its name, the numbers it carries (a
// cage's sum, a dot's value, an outside clue's total, a quadruple's digits), and Delete.
import type { ConstraintEntry, Rc } from '@zinc/shared';
import { useState } from 'react';
import { describeEntry, diagonalsFrom } from '../../lib/builder/model';
import { OptionPills } from './Palette';

interface Field {
  key: string;
  label: string;
  /** Empty takes the key off (a killer cage without a sum). */
  optional?: boolean;
  min?: number;
}

function fieldsOf(e: ConstraintEntry): Field[] {
  switch (e.type) {
    case 'killer':
      return [{ key: 'sum', label: 'Sum', optional: true, min: 1 }];
    case 'little-killer':
    case 'sandwich':
    case 'x-sum':
    case 'frame-sum':
    case 'battlefield':
      return [{ key: 'sum', label: 'Sum', min: 0 }];
    case 'skyscraper':
      return [{ key: 'count', label: 'Seen', min: 1 }];
    case 'difference':
    case 'ratio':
    case 'edge-sum':
      return [{ key: 'value', label: e.type === 'edge-sum' ? 'Sum' : 'Value', min: 1 }];
    case 'whispers':
    case 'lockout':
      return [{ key: 'difference', label: 'Difference', min: 1 }];
    case 'modular':
      return [{ key: 'modulus', label: 'Modulus', min: 2 }];
    default:
      return [];
  }
}

function NumberField({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [text, setText] = useState(value === null ? '' : String(value));
  // An undo or another edit changes the value: the field follows.
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setText(value === null ? '' : String(value));
  }
  return (
    <label className="flex items-center gap-2 text-[13px]">
      <span className="w-20 text-muted">{field.label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={field.min}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const v = e.target.value.trim() === '' ? null : Number(e.target.value);
          if (v === null ? field.optional : Number.isInteger(v) && v >= (field.min ?? 0))
            onChange(v);
        }}
        className="h-9 min-w-0 flex-1 rounded-[8px] border border-line bg-field px-2 text-[14px] text-ink"
      />
    </label>
  );
}

const ARROWS: Record<string, string> = { '1,1': '↘', '1,-1': '↙', '-1,1': '↗', '-1,-1': '↖' };

export function PieceCard({
  entry,
  rows,
  cols,
  digits,
  addedByApp,
  onChange,
  onDelete,
}: {
  entry: ConstraintEntry;
  rows: number;
  cols: number;
  digits: string[];
  addedByApp: boolean;
  onChange: (e: ConstraintEntry) => void;
  onDelete: () => void;
}) {
  const digitsText = Array.isArray(entry.digits) ? entry.digits.join(' ') : '';
  const [quad, setQuad] = useState(digitsText);
  const [seenDigits, setSeenDigits] = useState(digitsText);
  if (seenDigits !== digitsText) {
    setSeenDigits(digitsText);
    setQuad(digitsText);
  }
  const set = (key: string, v: unknown) => {
    const next: ConstraintEntry = { ...entry };
    if (v === null || v === undefined) delete next[key];
    else next[key] = v;
    if (next.type === 'edge-sum' && key === 'value') {
      if (v === 10) next.label = 'X';
      else if (v === 5) next.label = 'V';
      else delete next.label;
    }
    onChange(next);
  };
  const clue = Array.isArray(entry.clue) ? (entry.clue as Rc) : null;
  const dirs = entry.type === 'little-killer' && clue ? diagonalsFrom(clue, rows, cols) : [];
  const dir = Array.isArray(entry.direction) ? (entry.direction as number[]).join(',') : '1,1';
  return (
    <div
      aria-label="Selected piece"
      className="flex flex-col gap-2 rounded-[12px] border border-accent px-3 py-2.5"
    >
      <span className="text-[13px] font-semibold">{describeEntry(entry, rows, cols)}</span>
      {addedByApp && <span className="text-[12px] text-muted">Added by the app.</span>}
      {fieldsOf(entry).map((f) => (
        <NumberField
          key={f.key}
          field={f}
          value={typeof entry[f.key] === 'number' ? (entry[f.key] as number) : null}
          onChange={(v) => set(f.key, v)}
        />
      ))}
      {entry.type === 'killer' && (
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={entry.repeats === true}
            onChange={(e) => set('repeats', e.target.checked ? true : null)}
          />
          Digits may repeat
        </label>
      )}
      {dirs.length > 1 && (
        <OptionPills
          label="Direction"
          value={dir}
          options={dirs.map((d) => ({
            value: d.join(','),
            label: ARROWS[d.join(',')] ?? d.join(','),
          }))}
          onChange={(v) => set('direction', v.split(',').map(Number))}
        />
      )}
      {entry.type === 'quadruple' && (
        <label className="flex items-center gap-2 text-[13px]">
          <span className="w-20 text-muted">Digits</span>
          <input
            value={quad}
            placeholder="1 2 5"
            onChange={(e) => {
              setQuad(e.target.value);
              const list = e.target.value
                .split(/[\s,]+/)
                .filter((d) => d && digits.includes(d))
                .slice(0, 4);
              set('digits', list);
            }}
            className="h-9 min-w-0 flex-1 rounded-[8px] border border-line bg-field px-2 text-[14px] text-ink"
          />
        </label>
      )}
      <div className="flex gap-1.5">
        {entry.type === 'greater-than' && Array.isArray(entry.cells) && (
          <button
            type="button"
            onClick={() => set('cells', [...(entry.cells as Rc[])].reverse())}
            className="h-8 rounded-[8px] border border-line bg-surface px-3 text-[13px] font-medium text-ink"
          >
            Flip
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="h-8 rounded-[8px] border border-line bg-surface px-3 text-[13px] font-medium text-ink"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
